import ModalContainer from '@/components/molecules/ModalContainer/ModalContainer';
import { TContentType } from '@/types/Content.types';
import { Box, HStack, Text, Tooltip, VStack } from '@chakra-ui/react';
import { ArrowRight, FileText, Newspaper, Video } from 'lucide-react';
import React from 'react';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (type: TContentType) => void;
};

const OPTIONS: {
  type: TContentType;
  label: string;
  description: string;
  icon: typeof Video;
  disabled?: boolean;
}[] = [
  {
    type: 'video',
    label: 'Reel',
    description: 'Short reels up to 90 seconds.',
    icon: Video,
  },
  {
    type: 'post',
    label: 'Post',
    description: 'Short (300 characters max) textual content.',
    icon: FileText,
  },
  {
    type: 'article',
    label: 'Article',
    description: 'More complex textual content with images.',
    icon: Newspaper,
  },
];

const BrandedContentTypePicker = ({ isOpen, onClose, onSelect }: Props) => {
  return (
    <ModalContainer isOpen={isOpen} onClose={onClose} title="Choose content you want to create:">
      <VStack align="stretch" gap="16px" width="100%" marginTop="8px" marginBottom="8px">
        {OPTIONS.map(({ type, label, description, icon: Icon, disabled }) => {
          const card = (
            <HStack
              key={type}
              as="button"
              disabled={disabled}
              onClick={disabled ? undefined : () => onSelect(type)}
              width="100%"
              gap="18px"
              textAlign="left"
              padding="18px 22px"
              borderRadius="16px"
              backgroundColor="white"
              boxShadow="0px 4px 16px rgba(14, 0, 98, 0.06)"
              opacity={disabled ? 0.6 : 1}
              cursor={disabled ? 'not-allowed' : 'pointer'}
              transition="all 0.18s ease"
              _hover={
                disabled
                  ? {}
                  : { boxShadow: '0px 8px 22px rgba(14, 0, 98, 0.14)', transform: 'translateY(-1px)' }
              }
            >
              <Box color="primary.500" flexShrink={0}>
                <Icon size={30} strokeWidth={2} />
              </Box>
              <VStack align="flex-start" flex={1} gap="2px">
                <HStack gap="8px">
                  <Text fontSize="18px" fontWeight={700} color="text.darkBlue">
                    {label}
                  </Text>
                  {disabled && (
                    <Text
                      fontSize="10px"
                      fontWeight={700}
                      color="text.mediumGray"
                      textTransform="uppercase"
                      letterSpacing="0.5px"
                      border="1px solid"
                      borderColor="background.gray"
                      borderRadius="full"
                      paddingX="8px"
                      paddingY="1px"
                    >
                      Coming soon
                    </Text>
                  )}
                </HStack>
                <Text fontSize="13px" color="text.mediumBlue">
                  {description}
                </Text>
              </VStack>
              <Box color="text.darkBlue" flexShrink={0}>
                <ArrowRight size={22} strokeWidth={2} />
              </Box>
            </HStack>
          );

          return disabled ? (
            <Tooltip key={type} label="Coming soon" placement="top">
              {card}
            </Tooltip>
          ) : (
            card
          );
        })}
      </VStack>
    </ModalContainer>
  );
};

export default BrandedContentTypePicker;
